import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plane, BookOpen, Users, LifeBuoy, Calendar, ChevronRight,
  Sparkles, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/user`;

const DashboardHome = () => {
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes, bRes] = await Promise.all([
          axios.get(`${API}/profile`, { withCredentials: true }),
          axios.get(`${API}/bookings`, { params: { page: 1, limit: 5 }, withCredentials: true }),
        ]);
        if (cancelled) return;
        setProfile(pRes.data);
        const list = bRes.data?.bookings || bRes.data || [];
        setBookings(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Could not load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => {
    const total = profile?.total_bookings ?? bookings.length;
    const upcoming = bookings.filter((b) => {
      const status = (b.status || b.booking_status || '').toLowerCase();
      return status === 'confirmed' || status === 'pending_supplier_confirmation';
    }).length;
    const cancelled = bookings.filter((b) => (b.status || b.booking_status || '').toLowerCase() === 'cancelled').length;
    return { total, upcoming, cancelled };
  }, [profile, bookings]);

  const nextTrip = useMemo(() => {
    const upcoming = bookings.find((b) => {
      const status = (b.status || b.booking_status || '').toLowerCase();
      return status === 'confirmed' || status === 'pending_supplier_confirmation';
    });
    return upcoming || null;
  }, [bookings]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B375B]" />
      </div>
    );
  }

  const firstName = (profile?.full_name || '').trim().split(' ')[0] || 'Traveler';

  return (
    <div className="space-y-6" data-testid="b2c-dashboard-home">
      {/* Welcome strip */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1B375B] to-[#335E77] text-white p-6 md:p-8 shadow-sm">
        <div className="absolute -top-10 -right-10 opacity-10">
          <Plane className="w-48 h-48 -rotate-12" />
        </div>
        <div className="relative">
          <p className="text-sm text-white/80 mb-1">Welcome back,</p>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{firstName} 👋</h1>
          <p className="text-sm text-white/80">Here&apos;s what&apos;s happening with your travel.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <StatCard icon={BookOpen} label="Total bookings" value={stats.total} testid="stat-total-bookings" />
        <StatCard icon={Calendar} label="Upcoming trips" value={stats.upcoming} testid="stat-upcoming-trips" accent="text-emerald-600" />
        <StatCard icon={Sparkles} label="Cancelled" value={stats.cancelled} testid="stat-cancelled" accent="text-rose-600" />
      </div>

      {/* Next trip + recent bookings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {nextTrip ? (
            <NextTripCard booking={nextTrip} />
          ) : (
            <EmptyTripCard />
          )}

          <RecentBookings bookings={bookings} />
        </div>

        {/* Quick links */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide px-1">Quick links</h2>
          <QuickLink to="/user/dashboard/bookings" icon={BookOpen} label="My Bookings" />
          <QuickLink to="/user/dashboard/passengers" icon={Users} label="My Passengers" />
          <QuickLink to="/user/dashboard/tickets" icon={LifeBuoy} label="Help & Tickets" />
          <QuickLink to="/" icon={Plane} label="Search a new flight" accent />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid, accent = 'text-[#1B375B]' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={testid}>
    <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gray-50 ${accent} mb-2.5`}>
      <Icon className="w-4.5 h-4.5" />
    </div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-2xl font-bold ${accent}`}>{value}</div>
  </div>
);

const NextTripCard = ({ booking }) => {
  const route = booking.route || booking.flight_data?.route || '';
  const date = booking.travel_date || booking.flight_data?.travel_date;
  const pnr = booking.pnr || booking.flight_data?.pnr || 'N/A';
  const id = booking.booking_id || booking.id;
  const formattedDate = date
    ? new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="next-trip-card">
      <div className="px-5 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
        <div className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Next trip</div>
      </div>
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-gray-900 font-bold text-xl">
              <Plane className="w-5 h-5 text-[#1B375B] -rotate-45" />
              <span className="truncate">{route || 'Your upcoming trip'}</span>
            </div>
            <div className="text-sm text-gray-600 mt-1.5">{formattedDate}</div>
            <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
              <span>PNR: <span className="font-mono font-semibold text-gray-900">{pnr}</span></span>
              <span>•</span>
              <span>Booking ID: <span className="font-mono">{id}</span></span>
            </div>
          </div>
          <Link
            to="/user/dashboard/bookings"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#1B375B] text-white rounded-lg hover:bg-[#335E77] transition-colors"
            data-testid="next-trip-view-btn"
          >
            View <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const EmptyTripCard = () => (
  <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-8 text-center" data-testid="empty-trip-card">
    <Plane className="w-12 h-12 text-gray-300 mx-auto mb-3" />
    <h3 className="font-semibold text-gray-900 mb-1">No upcoming trips yet</h3>
    <p className="text-sm text-gray-500 mb-4">Ready to book your next adventure?</p>
    <Link
      to="/"
      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1B375B] text-white rounded-lg font-semibold hover:bg-[#335E77] transition-colors"
    >
      Search flights
    </Link>
  </div>
);

const RecentBookings = ({ bookings }) => {
  if (!bookings.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Recent bookings</h3>
        <Link to="/user/dashboard/bookings" className="text-xs text-[#1B375B] font-semibold hover:underline">
          View all
        </Link>
      </div>
      <ul className="divide-y divide-gray-100">
        {bookings.slice(0, 4).map((b) => {
          const id = b.booking_id || b.id;
          const route = b.route || b.flight_data?.route || '—';
          const status = (b.status || b.booking_status || '').toLowerCase();
          const statusLabel = status.replace(/_/g, ' ');
          const statusClass = status === 'confirmed' ? 'bg-emerald-50 text-emerald-700'
            : status === 'cancelled' ? 'bg-rose-50 text-rose-700'
            : 'bg-amber-50 text-amber-700';
          return (
            <li key={id} className="px-5 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3">
              <Plane className="w-4 h-4 text-gray-400 -rotate-45 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 truncate">{route}</div>
                <div className="text-xs text-gray-500 font-mono">{id}</div>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${statusClass}`}>{statusLabel || '—'}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const QuickLink = ({ to, icon: Icon, label, accent }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
      accent
        ? 'bg-[#1B375B] border-[#1B375B] text-white hover:bg-[#335E77]'
        : 'bg-white border-gray-200 hover:border-[#1B375B] hover:bg-gray-50 text-gray-800'
    }`}
  >
    <Icon className="w-4.5 h-4.5 shrink-0" />
    <span className="font-medium text-sm flex-1">{label}</span>
    <ChevronRight className="w-4 h-4 opacity-60" />
  </Link>
);

export default DashboardHome;
